import React, { useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { api } from "../../app/Api/ApiSlice";
import { clearCredentials } from "../../app/slices/authSlice";
import {
    setFeeds,
    resetFeeds,
    setPage,
    setHasMore,
    setScrollPosition
} from "../../app/slices/feedsSlice";

import Loader from "../../components/Loader";
import ProductAd from "../../components/adCards/Product";
import ShelfAd from "../../components/adCards/Shelf";
import PostAd from "../../components/adCards/Post";
import StoreAd from "../../components/adCards/Store";
import UserPostCard from "../../components/UserPostCard";
import OrgPostCard from "../../components/OrgPostCard";

import "./posts.css";

const Feeds = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { posts, page, hasMore, scrollPosition } = useSelector(
        (state) => state.feeds
    );

    const isFetching = useRef(false);
    const containerRef = useRef(null);
    const observerRef = useRef(null);
    const loaderRef = useRef(null);

    // ===== Fetch Feeds =====
    const fetchFeeds = useCallback(
        async (pageNum, isRefresh = false) => {
            if (isFetching.current) return;

            try {
                isFetching.current = true;

                const response = await api.get("/feeds", {
                    params: { page: pageNum }
                });

                if (response.status === 200) {
                    const fetchedData = response.data.feeds || [];

                    if (isRefresh) {
                        dispatch(resetFeeds(fetchedData));
                    } else {
                        dispatch(setFeeds(fetchedData));
                    }

                    dispatch(setHasMore(Boolean(response.data.hasMore)));
                }
            } catch (error) {
                const errorMsg =
                    error.response?.data?.message || "An error occurred";
                toast.error(errorMsg);
                dispatch(setHasMore(false));

                if (error.response?.status === 401) {
                    dispatch(clearCredentials());
                    navigate("/login");
                }
            } finally {
                isFetching.current = false;
            }
        },
        [dispatch, navigate]
    );

    // ===== Initial Load =====
    useEffect(() => {
        if (posts.length === 0) {
            fetchFeeds(1);
        }
    }, [fetchFeeds]);

    // ===== Restore Scroll Position =====
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = scrollPosition || 0;
        }
    }, [scrollPosition]);

    // ===== Save Scroll Position on Unmount =====
    useEffect(() => {
        return () => {
            if (containerRef.current) {
                dispatch(setScrollPosition(containerRef.current.scrollTop));
            }
        };
    }, [dispatch]);

    // ===== Load More Logic =====
    const loadMore = useCallback(() => {
        if (hasMore && !isFetching.current) {
            const nextPage = page + 1;
            dispatch(setPage(nextPage));
            fetchFeeds(nextPage);
        }
    }, [hasMore, page, dispatch, fetchFeeds]);

    // ===== IntersectionObserver Setup =====
    useEffect(() => {
        if (!loaderRef.current || !containerRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            {
                root: containerRef.current,
                threshold: 1.0
            }
        );

        observerRef.current.observe(loaderRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [loadMore]);

    // ===== Refresh =====
    const handleRefresh = () => {
        dispatch(setPage(1));
        fetchFeeds(1, true);
    };

    // ===== Render Post by Type =====
const renderPostCard = (post) => {
    switch (post.postType) {
        case "userPost":
            return <UserPostCard userPost={post} />;
        case "orgPost":
            return <OrgPostCard orgPost={post} />;
        case "postAd":
            return <PostAd ad={post} />;
        case "shelfAd":
            return <ShelfAd ad={post} />;
        case "storeAd":
            return <StoreAd ad={post} />;
        case "productAd":
            return <ProductAd ad={post} />;
        default:
            return null;
    }
};
// console.log(posts)
    return (
        <div className="feeds-container">
            {/* ===== Header ===== */}
            <header className="feeds-header">
                <div className="feeds-header-content">
                    <h1>Home</h1>
                    <Link to="/create" className="feeds-header-link">
                        <p>create</p>
                    </Link>
                </div>
            </header>

            {/* ===== Feed Content (Scroll Container) ===== */}
            <main
                className="feeds-content"
                ref={containerRef}
                style={{ overflowY: "auto", flex: 1 }}
            >
                {posts.map((post) => (
                    <div key={post._id} className="home-feed-post">
                        {renderPostCard(post)}
                    </div>
                ))}

                {/* Loader trigger element */}
                {hasMore && (
                    <div ref={loaderRef} className="feed-loader-wrapper">
                        <Loader />
                    </div>
                )}

                {!isFetching.current && posts.length === 0 && (
                    <div className="empty-state">
                        <p className="not-found-msg">No feeds available</p>
                    </div>
                )}

                <div className="bottom-box"></div>
            </main>
        </div>
    );
};



export default Feeds;